export type TaxDocumentKind = "cpf" | "cnpj";

export const CPF_LENGTH = 11;
export const CNPJ_LENGTH = 14;
export const CPF_MASK_MAX_LENGTH = 14;
export const CNPJ_MASK_MAX_LENGTH = 18;

const MASK_CHARS_REGEX = /[./-]/g;

export function stripTaxDocumentMask(value: string): string {
  return (value ?? "").replace(MASK_CHARS_REGEX, "");
}

export function detectTaxDocumentKind(value: string): TaxDocumentKind {
  const stripped = stripTaxDocumentMask(value).toUpperCase();

  if (/[A-Z]/.test(stripped)) {
    return "cnpj";
  }

  if (stripped.length > CPF_LENGTH) {
    return "cnpj";
  }

  return "cpf";
}

function applyCpfMask(digits: string): string {
  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return digits.replace(/(\d{3})(\d{1,3})/, "$1.$2");
  }

  if (digits.length <= 9) {
    return digits.replace(/(\d{3})(\d{3})(\d{1,3})/, "$1.$2.$3");
  }

  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, "$1.$2.$3-$4");
}

function applyCnpjMask(value: string): string {
  if (value.length <= 2) {
    return value;
  }

  if (value.length <= 5) {
    return `${value.slice(0, 2)}.${value.slice(2)}`;
  }

  if (value.length <= 8) {
    return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
  }

  if (value.length <= 12) {
    return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(
      5,
      8,
    )}/${value.slice(8)}`;
  }

  return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(
    5,
    8,
  )}/${value.slice(8, 12)}-${value.slice(12)}`;
}

function sanitizeCnpjBody(value: string): string {
  const upper = value.toUpperCase();
  const body = upper.slice(0, 12).replace(/[^A-Z0-9]/g, "");
  const dv = upper.slice(12).replace(/[^0-9]/g, "");

  return (body + dv).slice(0, CNPJ_LENGTH);
}

export function sanitizeCpfInput(value: string): string {
  return (value ?? "").replace(/\D/g, "").slice(0, CPF_LENGTH);
}

export function sanitizeTaxDocumentInput(value: string): string {
  const stripped = stripTaxDocumentMask(value ?? "");
  const kind = detectTaxDocumentKind(stripped);

  if (kind === "cpf") {
    return sanitizeCpfInput(stripped);
  }

  return sanitizeCnpjBody(stripped);
}

export function formatCpf(value: string | number | undefined): string {
  const digits = sanitizeCpfInput(String(value ?? ""));

  if (!digits) {
    return "";
  }

  return applyCpfMask(digits);
}

export function formatCnpj(value: string | number | undefined): string {
  const sanitized = sanitizeCnpjBody(String(value ?? ""));

  if (!sanitized) {
    return "";
  }

  return applyCnpjMask(sanitized);
}

export function formatTaxDocument(value: string | number | undefined): string {
  const raw = String(value ?? "");

  if (!raw) {
    return "";
  }

  const kind = detectTaxDocumentKind(raw);

  if (kind === "cpf") {
    return formatCpf(raw);
  }

  return formatCnpj(raw);
}

export function maskCpfOnInput(value: string): string {
  return formatCpf(sanitizeCpfInput(value));
}

export function maskTaxDocumentOnInput(value: string): string {
  return formatTaxDocument(sanitizeTaxDocumentInput(value));
}
